import React from 'react';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

/**
 * 1. Device Status Overview Chart
 * Renders fleet connectivity distribution strictly from real online & offline device counts.
 */
export const DeviceStatusChart = ({ online = 0, offline = 0 }) => {
  const total = (Number(online) || 0) + (Number(offline) || 0);

  if (total === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200/90 shadow-xs p-4 flex flex-col justify-between h-full min-h-[200px]">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 text-xs tracking-tight uppercase">Fleet Connectivity</h3>
        </div>
        <div className="flex flex-col items-center justify-center my-auto py-6 text-center">
          <div className="w-10 h-10 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 mb-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <span className="text-xs font-medium text-slate-500">No fleet telemetry data</span>
          <span className="text-[11px] text-slate-400 mt-0.5">Awaiting active device connections</span>
        </div>
      </div>
    );
  }

  const onlinePct = Math.round(((online || 0) / total) * 1000) / 10;
  const offlinePct = Math.round((100 - onlinePct) * 10) / 10;

  const data = {
    labels: ['Online', 'Offline'],
    datasets: [
      {
        data: [online || 0, offline || 0],
        backgroundColor: ['#10b981', '#cbd5e1'],
        borderWidth: 0,
        hoverOffset: 4,
        cutout: '74%',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#0f172a',
        padding: 10,
        cornerRadius: 8,
        titleFont: { size: 12, weight: '600' },
        bodyFont: { size: 11 },
        callbacks: {
          label: (context) => {
            const val = context.raw;
            const pct = Math.round((val / total) * 1000) / 10;
            return ` ${context.label}: ${val} (${pct}%)`;
          },
        },
      },
    },
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200/90 shadow-xs p-4 flex flex-col justify-between h-full">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-slate-800 text-xs tracking-tight uppercase">Fleet Connectivity</h3>
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
          {onlinePct}% Live
        </span>
      </div>

      <div className="relative flex items-center justify-center my-auto py-2">
        <div className="w-32 h-32 relative">
          <Doughnut data={data} options={options} />
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-2xl font-bold text-slate-800 leading-none">{total}</span>
            <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider mt-1">Devices</span>
          </div>
        </div>

        {/* Legend */}
        <div className="ml-5 space-y-2.5 text-xs flex-1 max-w-[130px]">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-slate-600 font-medium">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0"></span>
              Online
            </span>
            <span className="font-bold text-slate-900">{online}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-slate-600 font-medium">
              <span className="w-2.5 h-2.5 rounded-full bg-slate-300 shrink-0"></span>
              Offline
            </span>
            <span className="font-bold text-slate-900">{offline}</span>
          </div>
          <div className="pt-2 border-t border-slate-100 text-[11px] text-slate-500 font-medium">
            {offlinePct}% inactive
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 2. Production Summary / Trend Chart
 * Renders daily telemetry keycount trend from the real 7-day backend packet array.
 */
export const ProductionSummaryChart = ({ trendData = [], totalCuttings }) => {
  const hasTrendData = Array.isArray(trendData) && trendData.length > 0 && trendData.some(d => Number(d.keycount) > 0 || Number(d.energy) > 0);

  // If no trend data or total cuttings recorded
  if (!hasTrendData && (!totalCuttings || totalCuttings === 0)) {
    return (
      <div className="bg-white rounded-xl border border-slate-200/90 shadow-xs p-4 flex flex-col justify-between h-full min-h-[200px]">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold text-slate-800 text-xs tracking-tight uppercase">Cutting Volume (7 Days)</h3>
        </div>
        <div className="flex flex-col items-center justify-center my-auto py-6 text-center">
          <div className="w-10 h-10 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 mb-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <span className="text-xs font-medium text-slate-500">No cutting packets logged</span>
          <span className="text-[11px] text-slate-400 mt-0.5">Production telemetry will graph here automatically</span>
        </div>
      </div>
    );
  }

  // Separate the extra baseline day from the 7 displayed days
  let baselineKeycount = 0;
  let displayedDays = trendData;

  if (trendData.length >= 8) {
    const baselineItem = trendData[0];
    baselineKeycount = Number(baselineItem?.keycount) || 0;
    displayedDays = trendData.slice(1);
  } else if (trendData.some((d) => d.isBaseline)) {
    const baselineItem = trendData.find((d) => d.isBaseline);
    baselineKeycount = Number(baselineItem?.keycount) || 0;
    displayedDays = trendData.filter((d) => !d.isBaseline);
  }

  // Format labels for the 7 displayed days (e.g. "2026-09-17" -> "17 Sep")
  const labels = displayedDays.map((d) => {
    if (!d.date) return '';
    const parts = d.date.split('-');
    if (parts.length === 3) {
      const dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
      return dateObj.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    }
    return d.date;
  });

  // Calculate actual cuts completed per day (current day's max - previous day's max)
  let lastNonZeroMax = baselineKeycount > 0 ? baselineKeycount : null;

  const values = displayedDays.map((d) => {
    const rawVal = Number(d.keycount) || 0;

    // If a day has 0 or no cutting data, show 0 cuts and do not reset the previous maximum
    if (rawVal === 0) {
      return 0;
    }

    if (lastNonZeroMax === null) {
      // If no prior baseline day was recorded, establish baseline with current day's max
      // and show 0 cuts to prevent an artificially large historical odometer spike
      lastNonZeroMax = rawVal;
      return 0;
    }

    // Daily completed cuts = current day's max - previous day's (or last non-zero day's) max
    const dailyCuts = Math.max(0, rawVal - lastNonZeroMax);
    lastNonZeroMax = Math.max(lastNonZeroMax, rawVal);
    return dailyCuts;
  });

  const sum7DayCuts = values.reduce((acc, v) => acc + v, 0);

  const data = {
    labels,
    datasets: [
      {
        label: 'Cuts Completed',
        data: values,
        backgroundColor: '#3b82f6',
        hoverBackgroundColor: '#2563eb',
        borderRadius: 4,
        barPercentage: 0.5,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#0f172a',
        padding: 10,
        cornerRadius: 8,
        titleFont: { size: 12, weight: '600' },
        bodyFont: { size: 11 },
        callbacks: {
          label: (context) => ` ${context.raw.toLocaleString()} cuts completed`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { size: 10, weight: '500' },
          color: '#64748b',
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: '#f1f5f9' },
        ticks: {
          font: { size: 10 },
          color: '#94a3b8',
          precision: 0,
          callback: (value) => (value >= 1000 ? `${value / 1000}K` : value),
        },
      },
    },
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200/90 shadow-xs p-4 flex flex-col justify-between h-full">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-semibold text-slate-800 text-xs tracking-tight uppercase">Cutting Volume (7 Days)</h3>
        {sum7DayCuts > 0 && (
          <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200">
            {sum7DayCuts.toLocaleString()} cuts
          </span>
        )}
      </div>

      <div className="h-40 w-full pt-2">
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};

/**
 * 3. Top Machines Chart
 * Renders the top performing machines based on real cutting volumes from the API.
 */
export const TopMachinesChart = ({ machines = [] }) => {
  const validMachines = Array.isArray(machines)
    ? machines
        .filter((m) => m && (Number(m.keycount) > 0 || Number(m.count) > 0))
        .map((m) => ({
          id: m.deviceId || m.machSerial || m.id || 'Machine',
          count: Number(m.keycount) || Number(m.count) || 0,
          dealer: m.dealerName || m.dealer?.name || '',
        }))
    : [];

  if (validMachines.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200/90 shadow-xs p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800 text-xs tracking-tight uppercase">Top 10 Machines by Cuttings</h3>
        </div>
        <div className="py-6 text-center text-slate-400 text-xs">
          No cutting telemetry logged yet across machines
        </div>
      </div>
    );
  }

  const maxVal = Math.max(...validMachines.map((m) => m.count), 1);

  return (
    <div className="bg-white rounded-xl border border-slate-200/90 shadow-xs p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-800 text-xs tracking-tight uppercase">Top 10 Machines by Cuttings</h3>
        <span className="text-[11px] font-medium text-slate-400">Telemetry Volume</span>
      </div>

      <div className="space-y-2.5">
        {validMachines.slice(0, 10).map((m, idx) => {
          const pct = Math.min(100, Math.round((m.count / maxVal) * 100));
          return (
            <div key={m.id + idx} className="text-xs">
              <div className="flex items-center justify-between text-slate-600 font-medium mb-1">
                <div className="flex items-center gap-1.5 truncate pr-2">
                  <span className="w-4 h-4 rounded bg-slate-100 text-slate-600 text-[10px] font-bold flex items-center justify-center shrink-0">
                    {idx + 1}
                  </span>
                  <span className="font-mono text-sm font-medium text-slate-800 truncate">
                    {m.id}
                  </span>
                  {m.dealer && (
                    <span className="text-[10px] text-slate-400 truncate hidden sm:inline">
                      • {m.dealer}
                    </span>
                  )}
                </div>
                <span className="font-bold text-slate-900 shrink-0 font-mono">
                  {m.count.toLocaleString()}
                </span>
              </div>
              <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  style={{ width: `${pct}%` }}
                  className="h-full bg-blue-600 rounded-full transition-all duration-500"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

